import { ChangeDetectionStrategy, Component, computed, output, signal } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroFunnel, heroXMark } from '@ng-icons/heroicons/outline';
import { NgpMenuTrigger } from 'ng-primitives/menu';
import { UiBadge, UiButton, UiIconButton, UiMenuItem, UiMenuPanel } from '../../../../../shared/ui';

const EVENT_GROUPS: ReadonlyArray<{ key: string; label: string; eventTypes: readonly string[] }> = [
  {
    key: 'lifecycle',
    label: 'Ciclo',
    eventTypes: [
      'conversation.created',
      'conversation.resolved',
      'conversation.archived',
      'conversation.reopened',
      'conversation.snoozed',
      'conversation.status_changed',
      'conversation.priority_changed',
      'conversation.channel_unavailable',
    ],
  },
  { key: 'sessions', label: 'Sessões', eventTypes: ['session.opened', 'session.closed', 'session.assigned'] },
  { key: 'labels', label: 'Labels', eventTypes: ['conversation.label_added', 'conversation.label_removed'] },
];

export interface HistoryFiltersValue {
  eventTypes?: readonly string[];
  /** Reserved for future power-user mode; not surfaced in MVP. */
  actorTypes?: readonly string[];
  sessionId?: string;
}

/**
 * Compact filter trigger above the history list. Default state is a single discreet button
 * `Filtrar` (with a count badge once anything is selected). Clicking it opens a popover with
 * one toggle per event group — actors filter intentionally omitted from MVP per UX feedback.
 */
@Component({
  selector: 'app-history-filters',
  imports: [NgIcon, NgpMenuTrigger, UiBadge, UiButton, UiIconButton, UiMenuItem, UiMenuPanel],
  providers: [provideIcons({ heroFunnel, heroXMark })],
  template: `
    <div class="history-filters" #root>
      <button
        type="button"
        appButton
        [variant]="hasAny() ? 'secondary' : 'ghost'"
        size="sm"
        [ngpMenuTrigger]="filtersMenu"
        ngpMenuTriggerPlacement="bottom-start"
      >
        <ng-icon name="heroFunnel" size="12" aria-hidden="true" />
        <span>Filtrar</span>
        @if (count() > 0) {
          <span appBadge tone="accent" size="xs">{{ count() }}</span>
        }
      </button>
      @if (hasAny()) {
        <button type="button" appUiIconButton variant="ghost" size="xs" (click)="clear()" aria-label="Limpar filtros">
          <ng-icon name="heroXMark" size="12" aria-hidden="true" />
        </button>
      }

      <ng-template #filtersMenu>
        <div appMenuPanel class="history-filters__menu" role="dialog">
          <p class="history-filters__legend">Grupos de evento</p>
          <ul class="history-filters__list" role="listbox">
            @for (group of eventGroups; track group.key) {
              <li>
                <button
                  type="button"
                  appMenuItem
                  [ngpMenuItemCloseOnSelect]="false"
                  role="option"
                  class="history-filters__option"
                  [class.history-filters__option--active]="isGroupActive(group.key)"
                  [attr.aria-selected]="isGroupActive(group.key)"
                  (click)="toggleGroup(group.key)"
                >
                  <span class="history-filters__checkbox" aria-hidden="true"></span>
                  {{ group.label }}
                </button>
              </li>
            }
          </ul>
        </div>
      </ng-template>
    </div>
  `,
  styleUrl: './history-filters.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistoryFiltersComponent {
  protected readonly eventGroups = EVENT_GROUPS;

  private readonly selectedGroups = signal<readonly string[]>([]);

  readonly filtersChange = output<HistoryFiltersValue>();

  protected readonly count = computed(() => this.selectedGroups().length);
  protected readonly hasAny = computed(() => this.count() > 0);

  protected isGroupActive(key: string): boolean {
    return this.selectedGroups().includes(key);
  }

  protected toggleGroup(key: string): void {
    this.selectedGroups.update((groups) =>
      groups.includes(key) ? groups.filter((entry) => entry !== key) : [...groups, key],
    );
    this.emit();
  }

  protected clear(): void {
    this.selectedGroups.set([]);
    this.emit();
  }

  private emit(): void {
    const groups = this.selectedGroups();
    const eventTypes = groups.length === 0
      ? undefined
      : EVENT_GROUPS.filter((group) => groups.includes(group.key)).flatMap((group) => group.eventTypes);
    this.filtersChange.emit({ eventTypes });
  }
}
